import { Component, computed, inject, Signal } from '@angular/core';
import { DetailBaseComponent } from '@kinet-open-network/client/core/detail-base';
import {
  buildFlowActionMenuItems,
  FLOW_TYPE_CONFIGS,
} from '@kinet-open-network/client/flows/data-access';
import { LoadItemPricesMode } from '@kinet-open-network/client/product-prices/data-access';
import {
  confirmDialog,
  HeaderButton,
  IndicatorComponent,
  SidebarLayoutComponent,
  SidebarLayoutHeaderDirective,
  SidebarTabsConfig,
  toast,
} from '@kinet-open-network/client/shared/ui';
import { OrderStatus } from '@kinet-open-network/shared/data-access/model';
import {
  FlowType,
  goodsReceiptStatusOptions,
} from '@kinet-open-network/shared/data-access/model-pbgas';
import { MenuItem } from 'primeng/api';
import { FlowGoodsReceipts } from '../data-access/flow-goods-receipts.model';
import { FlowGoodsReceiptsService } from '../data-access/flow-goods-receipts.service';
import { FlowGoodsReceiptsStore } from '../data-access/flow-goods-receipts.store';

@Component({
  selector: 'kon-flow-goods-receipt-detail',
  imports: [
    SidebarLayoutComponent,
    SidebarLayoutHeaderDirective,
    IndicatorComponent,
  ],
  template: `
    <kon-sidebar-layout
      [actions]="actions()"
      [buttons]="headerButtons()"
      [style]="{ width: '90rem', 'max-width': '99%' }"
      [tabs]="tabsConfig"
    >
      @if (!isCreate()) {
        <div *konSidebarHeader>
          <kon-indicator
            [value]="store.model()?.status"
            [options]="statusOptions"
          ></kon-indicator>
        </div>
      }
    </kon-sidebar-layout>
  `,
})
export class FlowGoodsReceiptDetailComponent extends DetailBaseComponent<FlowGoodsReceipts> {
  store = inject(FlowGoodsReceiptsStore);
  service = inject(FlowGoodsReceiptsService);
  model = this.store.model;
  private flowConfig = inject(FLOW_TYPE_CONFIGS, { optional: true })?.find(
    (c) => c.type === FlowType.goodsReceipt,
  );

  private canEditPrices = computed(
    () =>
      !this.isRead() &&
      this.model()?.status !== OrderStatus.delivered &&
      !this.deleted(),
  );

  private async loadPrices(mode: LoadItemPricesMode) {
    if (!this.model()?.customerId) {
      toast({ severity: 'warn', detail: 'Najprv vyberte dodávateľa' });
      return;
    }
    try {
      const changed = await this.store.loadPrices(mode);
      toast({
        severity: changed ? 'success' : 'info',
        detail: changed ? 'Ceny boli aktualizované' : 'Ceny sa nezmenili',
      });
    } catch {
      toast({ severity: 'danger', detail: 'Nepodarilo sa aktualizovať ceny' });
    }
  }

  actions = computed<MenuItem[]>(() => [
    {
      label: 'Načítať zmluvné ceny',
      icon: 'pi pi-file-edit',
      visible: this.canEditPrices(),
      command: () => this.loadPrices('contract'),
    },
    {
      label: 'Načítať štandardné ceny',
      icon: 'pi pi-list',
      visible: this.canEditPrices(),
      command: () => this.loadPrices('standard'),
    },
    ...buildFlowActionMenuItems(this.store, this.flowConfig, this.ability()),
  ]);

  statusOptions = goodsReceiptStatusOptions;

  tabsConfig: SidebarTabsConfig = {
    tabs: [
      { value: 'default', label: 'Všeobecné', routerLink: ['.'] },
      {
        value: 'documents',
        label: 'Dokumenty',
        routerLink: ['documents'],
        visible: () => this.showDocumentsTab(),
      },
      {
        value: 'audits',
        label: 'Audit',
        routerLink: ['audits'],
        visible: () => !this.isCreate(),
      },
    ],
  };

  showDocumentsTab = computed(
    () => !this.isCreate() && this.ability().can('read', 'Document'),
  );

  headerButtons: Signal<HeaderButton[]> = computed(() => [
    {
      label: 'Potvrdiť',
      icon: 'pi pi-check',
      severity: 'primary',
      outlined: true,
      visible: this.model()?.status === OrderStatus.draft && this.isEdit(),
      action: () =>
        confirmDialog({
          header: 'Potvrdiť príjem tovaru',
          message: 'Naozaj chcete potvrdiť príjem tovaru?',
          icon: 'pi pi-check-circle',
          accept: () => this.store.processStep(this.model()!.id, {}),
        }),
    },
    {
      label: 'Prijať tovar',
      icon: 'pi pi-cart-arrow-down',
      severity: 'primary',
      outlined: true,
      visible: this.model()?.status === OrderStatus.approved && this.isEdit(),
      action: () =>
        this.router.navigate(['selection'], { relativeTo: this.route }),
    },
  ]);
}
